import { AuthContext, AUTH_STATUS } from '@kargotech/tms-core/auth';
import { PageSuspenseFallback } from '@kargotech/tms-ui/components';
import React, { useContext, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { NavigationContext } from '~/contexts/NavigationProvider';

import { ProfileContext } from '~/contexts/ProfileProvider';
import MainLayout from '~/layouts/MainLayout';

interface IPrivateRouteOpts {
  children?: React.ReactNode;
  Layout?: React.FC;
}

/**
 * Private Route: can only be accessed by authenticated user
 */
function PrivateRoute({
  children,
  Layout = MainLayout,
}: IPrivateRouteOpts) {
  const {
    authStatus,
    enrolledCompanies,
    isFirstTimeLogin,
    updateSessionStatus,
    selectedCompanyKsuid,
  } = useContext(AuthContext);
  const { getProfile, resetProfile } = useContext(ProfileContext);

  const { isNavigationBarVisible } = useContext(NavigationContext);

  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    updateSessionStatus();
  }, [updateSessionStatus]);

  useEffect(() => {
    if (authStatus === AUTH_STATUS?.AUTHENTICATED && selectedCompanyKsuid) {
      getProfile();
    }

    if (authStatus === AUTH_STATUS?.NOT_AUTHENTICATED) {
      resetProfile();
    }
  }, [authStatus, getProfile, resetProfile, selectedCompanyKsuid]);

  useEffect(() => {
    if (isFirstTimeLogin) {
      navigate('/welcome', { replace: true });
    }
  }, [isFirstTimeLogin, navigate]);

  if (authStatus === AUTH_STATUS?.LOADING) {
    return <PageSuspenseFallback />;
  }

  if (authStatus === AUTH_STATUS?.NOT_AUTHENTICATED || !selectedCompanyKsuid) {
    return (
      <Navigate
        state={{
          from: pathname,
          fromSearch: search,
        }}
        to="/auth/login"
      />
    );
  }

  if (authStatus === AUTH_STATUS?.AUTHENTICATED && !selectedCompanyKsuid && enrolledCompanies.length > 1) {
    navigate('/auth/choose-company', { replace: true });
    return null;
  }

  return (
    <>
      {!isNavigationBarVisible ? (
        <>
          {
          children || <Outlet />
        }
        </>
      ) : (
        <Layout>
          {
          children || <Outlet />
        }
        </Layout>
      )}
    </>
  );
}

export default PrivateRoute;
